// offlineStorage.js
import { API_BASE_URL } from '../config';

const OFFLINE_QUEUE_KEY = 'crystalPuzzle_offlineQueue';
const GAME_STATE_KEY = 'crystalPuzzle_gameState';

class OfflineStorageManager {
    constructor() {
        this.offlineQueue = this.loadOfflineQueue();
        this.isOnline = navigator.onLine;
        
        // Set up online/offline listeners
        window.addEventListener('online', () => {
            console.log('Connection restored, processing sync queue...');
            this.isOnline = true;
            this.processSyncQueue();
        });
        
        window.addEventListener('offline', () => {
            console.log('Connection lost, switching to offline mode...');
            this.isOnline = false;
        });
    }

    loadOfflineQueue() {
        try {
            const queue = localStorage.getItem(OFFLINE_QUEUE_KEY);
            return queue ? JSON.parse(queue) : [];
        } catch (error) {
            console.warn('Error loading offline queue:', error);
            return [];
        }
    }

    saveOfflineQueue() {
        try {
            localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(this.offlineQueue));
        } catch (error) {
            console.warn('Error saving offline queue:', error);
        }
    }

    async saveGameState(gameState) {
        const stateKey = GAME_STATE_KEY + '_' + gameState.userId + '_' + gameState.level;
        
        // Only save to local storage (no more HTTP calls)
        try {
            localStorage.setItem(stateKey, JSON.stringify({
                ...gameState,
                lastUpdated: new Date().toISOString()
            }));
        } catch (error) {
            console.warn('Error saving to local storage:', error);
        }
    }

    queueForSync(gameState) {
        this.offlineQueue.push({
            type: 'saveGameState',
            data: gameState,
            timestamp: new Date().toISOString()
        });
        this.saveOfflineQueue();
    }

    async updateProgress(progressData) {
        // Always store progress locally first
        try {
            const key = `progress_${progressData.userId}_${progressData.level}`;
            localStorage.setItem(key, JSON.stringify({
                ...progressData,
                timestamp: new Date().toISOString()
            }));
        } catch (error) {
            console.warn('Error saving progress locally:', error);
        }

        if (this.isOnline) {
            try {
                const endpoint = progressData.isHardMode 
                    ? `${API_BASE_URL}/progress/hard`
                    : `${API_BASE_URL}/progress`;

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(progressData)
                });

                if (!response.ok) {
                    this.queueProgressUpdate(progressData);
                }
                
                return true;
            } catch (error) {
                // Network error, queue for later sync
                this.queueProgressUpdate(progressData);
                console.log('Progress saved locally, will sync when online');
                return true; // Return true to allow game progression
            }
        } else {
            this.queueProgressUpdate(progressData);
            console.log('Progress saved locally, will sync when online');
            return true; // Allow progression even when offline
        }
    }

    queueProgressUpdate(progressData) {
        this.offlineQueue.push({
            type: 'updateProgress',
            data: progressData,
            timestamp: new Date().toISOString()
        });
        this.saveOfflineQueue();
    }

    async loadGameState(userId, level, isHardMode = false) {
        // Only load from local storage (no more HTTP calls)
        const localKey = GAME_STATE_KEY + '_' + userId + '_' + level;
        const localState = localStorage.getItem(localKey);
        return localState ? JSON.parse(localState) : null;
    }

    async processSyncQueue() {
        if (!this.isOnline || this.offlineQueue.length === 0) return;

        console.log(`Processing sync queue: ${this.offlineQueue.length} items`);
        
        const queue = [...this.offlineQueue];
        this.offlineQueue = [];
        this.saveOfflineQueue();

        for (const item of queue) {
            try {
                if (item.type === 'saveGameState') {
                    await this.saveGameState(item.data);
                } else if (item.type === 'updateProgress') {
                    await this.updateProgress(item.data);
                }
            } catch (error) {
                console.warn('Error processing sync queue item:', error);
                // Re-queue failed items
                this.offlineQueue.push(item);
            }
        }

        this.saveOfflineQueue();
        console.log('Sync queue processing complete');
    }

    getOfflineQueueSize() {
        return this.offlineQueue.length;
    }

    isOffline() {
        return !this.isOnline;
    }
}

export const offlineStorage = new OfflineStorageManager();
